/**
 * PeriodicReviewDashboard — periodic GMP document review queue (DMS-13, drafts/57).
 * Owner: Claude (DMS workstream). Route: /documents/periodic-reviews.
 * Surfaces the existing periodic_review.reviews table (schema V3) — every GMP
 * document's scheduled review, bucketed overdue / due-30 / due-90. Read-only.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import { apiFetch } from '../../api/client';

type Bucket = 'overdue' | 'due30' | 'due90' | 'scheduled' | 'done';

interface ReviewRow {
  reviewId: string; docNumber: string; title: string; version: string | null;
  ownerUserId: string; dueAtUtc: string | null; nextReviewDate: string | null;
  state: string; bucket: Bucket;
}
interface PeriodicReviews {
  items: ReviewRow[];
  summary: { total: number; overdue: number; due30: number; due90: number };
}

const BUCKET_STYLE: Record<Bucket, { bg: string; fg: string; label: string }> = {
  overdue:   { bg: '#FEE2E2', fg: '#B91C1C', label: 'Overdue' },
  due30:     { bg: '#FEF3C7', fg: '#92400E', label: 'Due ≤ 30 d' },
  due90:     { bg: '#E0F2FE', fg: '#075985', label: 'Due ≤ 90 d' },
  scheduled: { bg: '#F1F5F9', fg: '#475569', label: 'Scheduled' },
  done:      { bg: '#DCFCE7', fg: '#0E9F7E', label: 'Done' },
};

export function PeriodicReviewDashboard() {
  const { t, i18n } = useTranslation();
  const df = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }), [i18n.language]);
  const q = useQuery<PeriodicReviews>({
    queryKey: ['periodic-reviews'],
    queryFn: ({ signal }) => apiFetch<PeriodicReviews>('/api/v1/periodic-reviews', { signal }),
    staleTime: 30_000,
  });

  const chip = (label: string, value: number, b: Bucket | 'total') => {
    const s = b === 'total' ? { bg: '#EEF2FF', fg: '#3730A3' } : BUCKET_STYLE[b];
    return (
      <div style={{ border: `1px solid ${s.fg}22`, background: s.bg, color: s.fg, borderRadius: 10, padding: '12px 16px', minWidth: 120 }}>
        <div style={{ fontSize: 12, opacity: 0.8 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
      </div>
    );
  };

  return (
    <div style={{ padding: 'var(--space-6, 24px)', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHero
        eyebrow={t('dms.periodic.eyebrow', { defaultValue: 'Documents' })}
        title={t('dms.periodic.title', { defaultValue: 'Periodic Review Queue' })}
        subtitle={t('dms.periodic.subtitle', {
          defaultValue:
            'Every controlled document is reviewed on a cadence (EU GMP Ch.4). Overdue reviews need immediate attention.',
        })}
      />

      {q.isLoading && <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
      {q.isError && <p role="alert" style={{ color: '#B91C1C' }}>{t('dms.periodic.error', { defaultValue: 'Could not load periodic reviews.' })}</p>}

      {q.data && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {chip(t('dms.periodic.total', { defaultValue: 'Total' }), q.data.summary.total, 'total')}
            {chip(t('dms.dashboard.overdue', { defaultValue: 'Overdue' }), q.data.summary.overdue, 'overdue')}
            {chip(t('dms.dashboard.due30', { defaultValue: 'Due ≤ 30 days' }), q.data.summary.due30, 'due30')}
            {chip(t('dms.dashboard.due90', { defaultValue: 'Due ≤ 90 days' }), q.data.summary.due90, 'due90')}
          </div>

          <Card>
            <CardHeader>
              <h2 style={{ margin: 0, fontSize: 16 }}>{t('dms.periodic.reviews', { defaultValue: 'Reviews' })}</h2>
            </CardHeader>
            <CardBody>
              {q.data.items.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted, #6B7989)' }}>{t('dms.periodic.empty', { defaultValue: 'No periodic reviews scheduled.' })}</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--color-text-muted, #6B7989)', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '8px 10px' }}>{t('dms.periodic.col.doc', { defaultValue: 'Document' })}</th>
                      <th style={{ padding: '8px 10px' }}>{t('dms.periodic.col.version', { defaultValue: 'Version' })}</th>
                      <th style={{ padding: '8px 10px' }}>{t('dms.periodic.col.due', { defaultValue: 'Due' })}</th>
                      <th style={{ padding: '8px 10px' }}>{t('dms.periodic.col.state', { defaultValue: 'State' })}</th>
                      <th style={{ padding: '8px 10px' }}>{t('dms.periodic.col.status', { defaultValue: 'Status' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.items.map((r) => {
                      const s = BUCKET_STYLE[r.bucket];
                      return (
                        <tr key={r.reviewId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px' }}>
                            <strong>{r.docNumber}</strong>
                            <div style={{ color: 'var(--color-text-muted, #6B7989)' }}>{r.title}</div>
                          </td>
                          <td style={{ padding: '10px' }}>{r.version ?? '—'}</td>
                          <td style={{ padding: '10px' }}>{r.dueAtUtc ? df.format(new Date(r.dueAtUtc)) : '—'}</td>
                          <td style={{ padding: '10px' }}>{r.state}</td>
                          <td style={{ padding: '10px' }}>
                            <span style={{ background: s.bg, color: s.fg, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{s.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
